/**
 * HTTP-backed Social API implementation for PlaygroundHost
 *
 * All operations call Cloud Run directly via HTTP with Firebase Auth token.
 * QR code generation uses the `qrcode` npm package client-side (same as native client).
 *
 * ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost.
 * Do NOT import from barrel exports (social/index.ts).
 * This ensures Firebase is not bundled in production builds.
 */
import {
  CanShareFileResult,
  ComposeSocialPostOptions,
  ComposeSocialPostResult,
  FollowMePlatform,
  FollowMeResult,
  FOLLOW_ME_PLATFORMS,
  SocialApi,
  ShareFileOptions,
  ShareFileResult,
  ShareLinkResult,
  QRCodeResult,
  ShareClickData,
  ShareContentTarget,
  ShareIntent,
  ShareMetadata,
  SocialQRCodeOptions,
  SHARE_FILE_ALLOWED_MIME_TYPES,
  SHARE_FILE_MAX_SIZE_BYTES,
} from './SocialApi'
import {
  stripComposeControlChars,
  validateComposePost,
  validateShareFile,
  validateShareIntentParams,
  validateShareMetadata,
  validateShareTarget,
} from './validateShare'
import { buildRedditComposerUrl, buildXComposerUrl } from './composeUrl'
import { buildFollowMeUrl } from './followMeUrl'
import { getFirebaseClient } from '../firebase/firebaseClient'
import { getCloudRunUrl } from '../config/playground'
import { getPlaygroundGameSessionToken } from '../http/playgroundGameSession'

async function callCloudRun<TResponse>(
  method: string,
  endpoint: string,
  body?: unknown,
): Promise<TResponse> {
  const client = await getFirebaseClient()
  const idToken = await client.getIdTokenOrThrow()
  const baseUrl = getCloudRunUrl()
  const url = `${baseUrl}${endpoint}`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  }

  const options: RequestInit = {
    method,
    headers,
  }

  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }

  const gst = await getPlaygroundGameSessionToken()
  if (gst) headers['x-h5-game-session'] = gst

  const response = await fetch(url, options)

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Cloud Run request failed (${response.status})`
    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.error || errorJson.message || errorMessage
    } catch {
      if (errorText) {
        errorMessage = errorText
      }
    }
    throw new Error(errorMessage)
  }

  const text = await response.text()
  if (!text) {
    return {} as TResponse
  }

  return JSON.parse(text) as TResponse
}

interface CreateShareResult {
  shareLinkId: string;
  shareUrl: string;
}

export class HttpSocialApi implements SocialApi {
  private readonly gameId: string

  constructor(gameId: string) {
    this.gameId = gameId
  }

  private async createShare(
    shareParams: Record<string, string>,
    metadata?: ShareMetadata,
    slug?: string,
    target?: ShareContentTarget,
  ): Promise<CreateShareResult> {
    validateShareTarget(target)
    // Same scoping rule as the app host, and the server rejects a gameId sent
    // alongside a target.
    const scopedTarget = target
      ? { format: target.format, contentId: `${this.gameId}:${target.entryId}` }
      : undefined
    // Omit shareLinkId so the server mints a short code (this is a direct-to-v1
    // transport with no legacy-endpoint path). Use the server-returned id below.
    const result = await callCloudRun<{
      success: boolean;
      shareLinkId: string;
      shareUrl: string | null;
      message?: string;
    }>('POST', '/v1/shares', {
      ...(scopedTarget ? { target: scopedTarget } : { gameId: this.gameId }),
      shareParams,
      metadata,
      slug,
    })

    if (!result.shareUrl) {
      throw new Error(
        '[RUN] Server returned null shareUrl — environment is likely misconfigured (missing prettyUrlPrefix or share link template).',
      )
    }

    return { shareLinkId: result.shareLinkId, shareUrl: result.shareUrl }
  }

  async shareLinkAsync(options: {
    shareParams: Record<string, string>;
    metadata?: ShareMetadata;
    slug?: string;
    target?: ShareContentTarget;
  }): Promise<ShareLinkResult> {
    const { shareUrl, shareLinkId } = await this.createShare(
      options.shareParams,
      options.metadata,
      options.slug,
      options.target,
    )

    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl)
      } catch (error) {
        console.warn('[RUN] Failed to copy share URL to clipboard', error)
      }
    }

    return { shareUrl, shareLinkId }
  }

  async createQRCodeAsync(options: {
    shareParams: Record<string, string>;
    metadata?: ShareMetadata;
    qrOptions?: SocialQRCodeOptions;
    slug?: string;
    target?: ShareContentTarget;
  }): Promise<QRCodeResult> {
    const { shareUrl, shareLinkId } = await this.createShare(
      options.shareParams,
      options.metadata,
      options.slug,
      options.target,
    )

    const QRCode = await import('qrcode')

    const {
      size = 256,
      margin = 2,
      format = 'png',
    } = options.qrOptions ?? {}

    let qrCode: string
    if (format === 'svg') {
      const svgMarkup = await QRCode.toString(shareUrl, {
        type: 'svg',
        width: size,
        margin,
      })
      qrCode = `data:image/svg+xml;utf8,${encodeURIComponent(svgMarkup)}`
    } else {
      qrCode = await QRCode.toDataURL(shareUrl, {
        width: size,
        margin,
      })
    }

    return { shareUrl, qrCode, shareLinkId }
  }

  // The playground has no host share button; validate exactly like production
  // so games surface cap violations in dev, then no-op.
  async setShareIntentAsync(options: ShareIntent): Promise<void> {
    validateShareIntentParams(options.shareParams)
    validateShareMetadata(options.metadata)
    validateShareTarget(options.target)
  }

  async clearShareIntentAsync(): Promise<void> {}

  async addShareClickDataAsync(options: {
    shareLinkId: string;
    metadata: Record<string, string>;
  }): Promise<void> {
    await callCloudRun<void>(
      'PUT',
      `/v1/shares/${options.shareLinkId}/clicks`,
      { metadata: options.metadata },
    )
  }

  async getShareClicksAsync(options: {
    shareLinkId: string;
  }): Promise<{ clicks: ShareClickData[]; truncated: boolean }> {
    return callCloudRun<{ clicks: ShareClickData[]; truncated: boolean }>(
      'GET',
      `/v1/shares/${options.shareLinkId}/clicks`,
    )
  }

  async getMyShareClickDataAsync(options: {
    shareLinkId: string;
  }): Promise<ShareClickData | null> {
    const result = await callCloudRun<{ data: ShareClickData | null }>(
      'GET',
      `/v1/shares/${options.shareLinkId}/clicks/me`,
    )
    return result.data
  }

  async shareFileAsync(options: ShareFileOptions): Promise<ShareFileResult> {
    const { data, filename, mimeType, title, text } = options

    if (!(SHARE_FILE_ALLOWED_MIME_TYPES as readonly string[]).includes(mimeType)) {
      throw new Error(
        `unsupported MIME type "${mimeType}". Allowed: ${SHARE_FILE_ALLOWED_MIME_TYPES.join(', ')}`,
      )
    }

    const blob = this.toBlob(data, mimeType)
    if (blob.size > SHARE_FILE_MAX_SIZE_BYTES) {
      throw new Error(
        `file size (${blob.size} bytes) exceeds max allowed (${SHARE_FILE_MAX_SIZE_BYTES} bytes)`,
      )
    }

    const file = new File([blob], filename, { type: mimeType })

    if (typeof navigator === 'undefined' || !navigator.share) {
      throw new Error('File sharing is not supported on this platform')
    }

    if (navigator.canShare && !navigator.canShare({ files: [file] })) {
      throw new Error('File sharing is not supported on this platform')
    }

    try {
      await navigator.share({ files: [file], title, text })
      return { cancelled: false }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { cancelled: true }
      }
      throw error
    }
  }

  private toBlob(data: Blob | ArrayBuffer | string, mimeType: string): Blob {
    if (data instanceof Blob) return data
    if (data instanceof ArrayBuffer) return new Blob([data], { type: mimeType })

    const binaryString = atob(data)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return new Blob([bytes], { type: mimeType })
  }

  async composeSocialPostAsync(
    options: ComposeSocialPostOptions,
  ): Promise<ComposeSocialPostResult> {
    const text = stripComposeControlChars(options.text ?? '')
    const title =
      options.title === undefined
        ? undefined
        : stripComposeControlChars(options.title)
    validateComposePost({ text, title, subreddit: options.subreddit })
    if (options.media) validateShareFile(options.media)

    const link = options.shareParams
      ? (await this.createShare(options.shareParams, options.metadata)).shareUrl
      : undefined
    const caption = link ? `${text}\n\n${link}` : text

    // Share-sheet destinations (TikTok/Instagram) need media routed through
    // the Web Share API — same direct-from-page pattern as shareFileAsync.
    // When the surface can't share files, degrade to the composer path below
    // instead of throwing (games never see an "unsupported" error).
    if (
      (options.platform === 'tiktok' || options.platform === 'instagram') &&
      options.media
    ) {
      const file = new File(
        [this.toBlob(options.media.data, options.media.mimeType)],
        options.media.filename,
        { type: options.media.mimeType },
      )
      const canShare =
        typeof navigator !== 'undefined' &&
        !!navigator.share &&
        (!navigator.canShare || navigator.canShare({ files: [file] }))
      if (canShare) {
        if (navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(caption)
          } catch (error) {
            console.warn('[RUN] Failed to copy caption to clipboard', error)
          }
        }
        try {
          await navigator.share({ files: [file], text: caption })
          return { completed: true, destination: options.platform }
        } catch (error: unknown) {
          if (error instanceof Error && error.name === 'AbortError') {
            return { completed: false, destination: options.platform }
          }
          throw error
        }
      }
    }

    const destination = options.platform === 'reddit' ? 'reddit' : 'x'
    const url =
      destination === 'reddit'
        ? buildRedditComposerUrl({ text, link, title, subreddit: options.subreddit })
        : buildXComposerUrl(text, link)

    const opened =
      typeof window !== 'undefined' && typeof window.open === 'function'
        ? window.open(url, '_blank', 'noopener,noreferrer')
        : null
    if (!opened) {
      // Popup blocked (user activation can expire across the share-mint
      // await) — leave the composer URL on the clipboard instead, and report
      // an honest non-completion: no composer actually opened.
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(url)
        } catch (error) {
          console.warn('[RUN] Failed to copy composer URL to clipboard', error)
        }
      }
      console.warn(`[RUN] Composer popup blocked — URL copied to clipboard: ${url}`)
      return { completed: false, destination }
    }

    return { completed: true, destination }
  }

  async openXFollowMeLinkAsync(): Promise<FollowMeResult> {
    return this.openFollowMeLink('x')
  }

  async openInstagramFollowMeLinkAsync(): Promise<FollowMeResult> {
    return this.openFollowMeLink('instagram')
  }

  async openTikTokFollowMeLinkAsync(): Promise<FollowMeResult> {
    return this.openFollowMeLink('tiktok')
  }

  async openDiscordFollowMeLinkAsync(): Promise<FollowMeResult> {
    return this.openFollowMeLink('discord')
  }

  async getConfiguredFollowMePlatformsAsync(): Promise<FollowMePlatform[]> {
    const handles = await this.fetchSocialHandles()
    return FOLLOW_ME_PLATFORMS.filter((p) => typeof handles[p] === 'string' && handles[p]!.length > 0)
  }

  private async fetchSocialHandles(): Promise<Partial<Record<FollowMePlatform, string>>> {
    const game = await callCloudRun<{
      creatorFollowMeHandles?: Partial<Record<FollowMePlatform, string>>
    }>('GET', `/v1/games/${this.gameId}`)
    return game.creatorFollowMeHandles ?? {}
  }

  private async openFollowMeLink(platform: FollowMePlatform): Promise<FollowMeResult> {
    const handle = (await this.fetchSocialHandles())[platform]
    if (!handle) {
      return { completed: false, reason: 'not_configured' }
    }
    // Playground runs in-page (no sandboxed iframe), so opening directly works.
    const url = buildFollowMeUrl(platform, handle)
    if (typeof window === 'undefined' || typeof window.open !== 'function') {
      console.warn(`[RUN] follow-me URL: ${url}`)
      return { completed: true }
    }
    // User activation can expire across the handle-fetch await, so a blocked
    // popup returns null — report an honest non-completion, matching compose.
    const opened = window.open(url, '_blank', 'noopener,noreferrer')
    return { completed: opened != null }
  }

  async canShareFileAsync(): Promise<CanShareFileResult> {
    if (typeof navigator === 'undefined' || !navigator.share || !navigator.canShare) {
      return { supported: false }
    }
    try {
      const probeTypes = ['image/png', 'video/mp4'] as const
      for (const type of probeTypes) {
        const ext = type.split('/')[1]
        const testFile = new File([''], `test.${ext}`, { type })
        if (navigator.canShare({ files: [testFile] })) {
          return { supported: true }
        }
      }
      return { supported: false }
    } catch {
      return { supported: false }
    }
  }
}
